import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';
import React from 'react';
import { Search, X } from 'react-feather';
import { usePrefixTranslation } from '../../i18n';
import { theme } from '../../theme/theme';
import { debounce } from '../../util/event';
import { Button, Buttons } from './forms/Button';
import { Select } from './forms/Select';
import { Checkbox } from './forms/Toggle';
import { motions } from './motions/motions';

const useFilterBarClasses = makeStyles({
    root: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 4,
    },
});

/**
 * Filter bar component that renders all internal filtering widgets.
 *
 * @param props.widgets widgets props (see widgets documentation)
 */
export const FilterBar = (props: {
    widgets: (
        | { type: 'clear'; visible?: boolean; onClick?: () => void }
        | ({ type: 'buttons' } & Parameters<typeof ButtonFilter>[0])
        | ({ type: 'checker' } & Parameters<typeof CheckerFilter>[0])
        | ({ type: 'select' } & Parameters<typeof SelectFilter>[0])
        | ({ type: 'search' } & Parameters<typeof SearchBox>[0])
    )[];
}) => {
    const classes = useFilterBarClasses();
    const widgets = {
        clear: (p: { visible?: boolean; onClick?: () => void }, i: number) => (
            <ClearFilter key={i} {...p} widgets={props.widgets} />
        ),
        buttons: (p: Parameters<typeof ButtonFilter>[0], i: number) => <ButtonFilter key={i} {...p} />,
        checker: (p: Parameters<typeof CheckerFilter>[0], i: number) => <CheckerFilter key={i} {...p} />,
        select: (p: Parameters<typeof SelectFilter>[0], i: number) => <SelectFilter key={i} {...p} />,
        search: (p: Parameters<typeof SearchBox>[0], i: number) => <SearchBox key={i} {...p} />,
    };

    return (
        <div className={classes.root}>{props.widgets.map((widget, i) => widgets[widget.type](widget as any, i))}</div>
    );
};

/**
 * A clear widget will try to remove all checker and select filters.
 *
 * @param props.widgets all filter bar widgets
 * @param props.visible if the widget is visible when there is no filter selected
 * @param props.onClick callback triggered when the widget is clicked (the filters will already be cleaned)
 */
const ClearFilter = (props: {
    widgets: Parameters<typeof FilterBar>[0]['widgets'];
    visible?: boolean;
    onClick?: () => void;
}) => {
    const { t } = usePrefixTranslation('global', { global: 'FilterBar' });
    const visible = props.widgets.reduce(
        (acc, w) =>
            acc ||
            (w.type === 'checker' && w.checked) ||
            (w.type === 'select' && w.selected.reduce((acc, s) => acc || s, false)),
        false,
    );

    const clearFilters = () =>
        props.widgets.forEach(w =>
            w.type === 'checker' ? w.onChange?.(false) : w.type === 'select' ? w.onSelected?.([]) : undefined,
        );

    if (!visible) return <></>;
    return (
        <Button theme='lighter' icon onClick={() => (clearFilters(), props.onClick?.())}>
            <X /> {t('clear-all')}
        </Button>
    );
};

/**
 * Buttons widget for the filter bar.
 * All buttons children are expected to be icons.
 *
 * @param props.buttons list of buttons data with content to render, if the buttons is selected, and click action
 */
const ButtonFilter = (props: {
    buttons: { children: React.ReactNode; selected?: boolean; onClick?: () => void }[];
}) => (
    <Buttons buttonProps={{ icon: true }}>
        {props.buttons.map((button, i) => (
            <Button key={i} {...button} theme={button.selected ? 'primary' : 'light'} />
        ))}
    </Buttons>
);

const useCheckerClasses = makeStyles({
    root: {
        '&.checked': { color: theme.colors.darkBlue[5] },
    },
    checkbox: {
        margin: '-12px 0',
        '& > label': { color: 'inherit' },
    },
});

/**
 * Checker widget for the filter bar.
 * The user can click anywhere in the container to toggle the checkbox.
 * This component is a controlled input
 *
 * @param props.label checkbox label
 * @param props.checked if the checkbox is checked
 * @param props.onChange callback triggered when the checked state changes
 */
const CheckerFilter = (props: { label: string; checked?: boolean; onChange?: (checked: boolean) => void }) => {
    const classes = useCheckerClasses();
    const cls = { checked: props.checked };

    return (
        <Button className={clsx(classes.root, cls)} theme='light' icon onClick={() => props.onChange?.(!props.checked)}>
            <Checkbox rootProps={{ className: classes.checkbox }} checked={!!props.checked} label={props.label} />
        </Button>
    );
};

const useSelectClasses = makeStyles({
    inside: {
        cursor: 'pointer',
        '&.selected, &.open, &:hover': { color: theme.colors.darkBlue[5] },
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        ...theme.fonts.button,
        '& > div': {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: 24,
            minHeight: 24,
            width: 24,
            height: 24,
            margin: -1,
            borderRadius: '50%',
            background: theme.colors.darkBlue[5],
            color: 'white',
            ...theme.fonts.caption,
        },
    },
});

/**
 * Select filter widget.
 *
 * @param props `props` are a subset of `Select` `props`, please check `Select` documentation for more details
 */
const SelectFilter = <T extends any>(props: {
    label: string;
    selected: boolean[];
    options: T[];
    multi?: boolean;
    all?: boolean;
    unselect?: boolean;
    render?: (option: T, on: 'input' | 'popup') => React.ReactNode;
    search?: (option: T) => string;
    searchLabel?: string;
    onSelected?: (option: boolean[]) => void;
}) => {
    const classes = useSelectClasses();
    const count = props.selected.reduce((acc, s) => acc + +s, 0);
    const cls = { selected: count > 0 };

    return (
        <Select
            theme='light'
            insideProps={{ className: clsx(classes.inside, cls) }}
            left={
                <div className={classes.label}>
                    <span>{props.label}</span>
                    {count > 0 && <div>{count}</div>}
                </div>
            }
            {...props}
            render={(option, on) => on !== 'input' && (props.render?.(option, on) ?? String(option))}
            label={undefined}
        />
    );
};

const useSearchBoxClasses = makeStyles({
    root: {
        position: 'relative',
        display: 'flex',
        flex: 1,
        gap: 10,
        padding: 13,
        borderRadius: 4,
        background: 'white',
        color: theme.colors.darkBlue[5],
        cursor: 'text',
        ...theme.fonts.label,
        '&.focus': {
            boxShadow: '0 0 0 1px #38489E inset',
            color: theme.colors.neutral[1],
        },
    },
    input: {
        flex: 1,
        color: 'inherit',
        border: 'none',
        '&::placeholder': {
            color: theme.colors.darkBlue[5],
        },
        '&:focus-visible': {
            outline: 'none',
        },
    },
    clear: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: 'pointer',
        ...theme.fonts.button,
        '& > svg': {
            width: 16,
            height: 16,
        },
    },
    suggestions: {
        position: 'absolute',
        left: 0,
        right: 0,
        top: 'calc(100% + 8px)',
        zIndex: 5,
        display: 'flex',
        flexDirection: 'column',
        padding: '8px 0',
        borderRadius: 4,
        boxShadow: '0 3px 10px -5px #1B1F3566',
        background: 'white',
        cursor: 'default',
    },
    item: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 16,
        ...theme.fonts.label,
        '&:hover': {
            background: '#EBEDF5',
        },
        '& > span': {
            color: theme.colors.darkBlue[5],
            '& > span:nth-child(2)': {
                fontWeight: 700,
            },
        },
        '& > svg': {
            width: 16,
            height: 16,
            color: theme.colors.neutral[2],
        },
        '& > svg:last-of-type': {
            cursor: 'pointer',
        },
    },
});

/**
 * Search bar widget for the filter bar.
 * This component automatically saves suggestions when the user presses the enter key.
 * Matching suggestions are displayed when new queries are being typed.
 *
 * @param props.label the search bar label
 * @param props.debounce interval in milliseconds to enable debounce of calls to `props.onChange`
 * @param props.autocompleteKey key used to store user suggestions in the local storage
 * @param props.onChange callback triggered when the query string is changed
 */
const SearchBox = (props: {
    label?: string;
    debounce?: number;
    autocompleteKey?: string;
    onChange?: (query: string) => void;
}) => {
    const classes = useSearchBoxClasses();
    const input$ = React.useRef<HTMLInputElement>();
    const { t } = usePrefixTranslation('global', { global: 'FilterBar' });
    const [value, setValue] = React.useState('');
    const [focus, setFocus] = React.useState(false);
    const storageKey = `search-box:${props.autocompleteKey}`;
    const [suggestions, setSuggestions] = React.useState<string[]>([]);
    const [matches, setMatches] = React.useState<{ suggestion: string; index: number }[]>([]);
    const onChange = React.useCallback(debounce(props.onChange, props.debounce ?? 0), [props.onChange, props.debounce]);
    const cls = { focus };

    React.useEffect(() => onChange(value), [value]);
    React.useEffect(() => setSuggestions((window.localStorage.getItem(storageKey) ?? '').split('|')), [storageKey]);
    React.useEffect(() => {
        setMatches(
            suggestions
                .map(suggestion => ({ suggestion, index: suggestion.indexOf(value) }))
                .filter(match => match.index !== -1)
                .sort((a, b) => a.index - b.index),
        );
    }, [value, suggestions]);

    const updateSuggestions = (suggestion: string, operation: 'insert' | 'remove') => {
        suggestion = suggestion.trim().slice(0, 50).toLowerCase().replaceAll(/\s+/g, ' ');
        const suggestions = (window.localStorage.getItem(storageKey) ?? '').split('|').slice(0, 100);
        const index = suggestions.indexOf(suggestion);
        if (index !== -1) suggestions.splice(index, 1);
        if (operation === 'insert') suggestions.unshift(suggestion);
        window.localStorage.setItem(storageKey, suggestions.join('|'));
        setSuggestions(suggestions);
    };

    return (
        <div className={clsx(classes.root, cls)}>
            <Search />
            <input
                ref={input$ as any}
                className={classes.input}
                placeholder={props.label}
                value={value}
                onChange={e => setValue(e.target.value)}
                onFocus={() => setFocus(true)}
                onBlur={() => setTimeout(() => setFocus(false), 250)}
                onKeyUp={e => {
                    if (e.key !== 'Enter') return;
                    input$.current?.blur();
                    updateSuggestions((e.target as any).value, 'insert');
                }}
            />
            <AnimatePresence>
                {!focus && value.length > 0 && (
                    <motion.div
                        key='clear'
                        className={classes.clear}
                        {...motions.presence({ slide: 10 })}
                        onClick={() => (setValue(''), input$.current?.blur())}
                    >
                        <X /> {t('clear-search')}
                    </motion.div>
                )}
                {focus && value.length > 0 && matches.length > 0 && (
                    <motion.div key='suggestions' className={classes.suggestions} {...motions.presence({ pop: 10 })}>
                        {matches.map(({ suggestion, index }) => (
                            <div
                                key={suggestion}
                                className={classes.item}
                                onClick={e => !e.defaultPrevented && setValue(suggestion)}
                            >
                                <Search />
                                <span>
                                    <span>{suggestion.slice(0, index)}</span>
                                    <span>{suggestion.slice(index, index + value.length)}</span>
                                    <span>{suggestion.slice(index + value.length)}</span>
                                </span>
                                <div style={{ flex: 1 }} />
                                <X onClick={e => (e.preventDefault(), updateSuggestions(suggestion, 'remove'))} />
                            </div>
                        ))}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};
